import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { JsonEditor } from '../../_shared/components/JsonEditor';
import { consumerFormSchema } from '../schema/consumerSchema';
import { useCreateConsumer, useReplaceConsumer } from '../hooks/useConsumers';
import type { ConsumerFormValues } from '../schema/consumerSchema';
import type { ConsumerRow } from '../types/consumer';

interface ConsumerFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  initial?: ConsumerRow | null;
  onClose: () => void;
  onSaved?: () => void;
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        padding: 'var(--spacing-2) var(--spacing-3)',
        border: 'none',
        borderBottom: active ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
        background: 'none',
        color: active ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
        cursor: 'pointer',
        fontSize: 'var(--font-size-sm)',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  );
}

const DEFAULT_VALUES: ConsumerFormValues = {
  username: '',
  customId: '',
  enabled: true,
};

export function ConsumerFormDialog({ open, mode, initial, onClose, onSaved }: ConsumerFormDialogProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'basic' | 'json'>('basic');
  const [toast, setToast] = useState<string | null>(null);

  const form = useForm<ConsumerFormValues>({
    resolver: zodResolver(consumerFormSchema),
    defaultValues: initial
      ? { username: initial.username, customId: initial.customId ?? '', enabled: initial.enabled }
      : DEFAULT_VALUES,
  });

  const createConsumer = useCreateConsumer((name) => {
    setToast(t('gateway.consumers.toast.created', { name }));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });
  const replaceConsumer = useReplaceConsumer(initial?.id ?? '', () => {
    setToast(t('gateway.consumers.toast.updated'));
    setTimeout(() => { setToast(null); onSaved?.(); onClose(); }, 1500);
  });

  useEffect(() => {
    if (open) {
      setActiveTab('basic');
      form.reset(initial
        ? { username: initial.username, customId: initial.customId ?? '', enabled: initial.enabled }
        : DEFAULT_VALUES);
    }
  }, [open, initial, form]);

  const watchedValues = form.watch();
  const isPending = createConsumer.isPending || replaceConsumer.isPending;

  const onSubmit = form.handleSubmit((values) => {
    if (mode === 'create') {
      createConsumer.mutate(values);
    } else if (initial) {
      replaceConsumer.mutate(values);
    }
  });

  const inputStyle = (hasError: boolean): React.CSSProperties => ({
    width: '100%',
    padding: 'var(--spacing-2) var(--spacing-3)',
    border: `1px solid ${hasError ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    boxSizing: 'border-box',
  });

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 500,
    marginBottom: 'var(--spacing-1)',
  };

  const fieldStyle: React.CSSProperties = { marginBottom: 'var(--spacing-3)' };

  return (
    <FormDialog
      open={open}
      title={mode === 'create' ? t('gateway.consumers.form.create') : t('gateway.consumers.form.edit')}
      onClose={onClose}
      onSubmit={onSubmit}
      submitLabel={isPending ? t('gateway.shared.actions.saving') : t('gateway.shared.actions.save')}
      cancelLabel={t('gateway.shared.actions.cancel')}
    >
      {toast && (
        <div role="alert" style={{ marginBottom: 'var(--spacing-3)', padding: 'var(--spacing-2) var(--spacing-3)', background: 'var(--color-success)', color: 'var(--color-text-inverse)', borderRadius: 'var(--radius-md)', fontSize: 'var(--font-size-sm)' }}>
          {toast}
        </div>
      )}

      {/* Tabs */}
      <div role="tablist" style={{ display: 'flex', borderBottom: '1px solid var(--color-surface-border)', marginBottom: 'var(--spacing-4)' }}>
        <TabButton active={activeTab === 'basic'} onClick={() => setActiveTab('basic')}>{t('gateway.consumers.form.tabs.basic')}</TabButton>
        <TabButton active={activeTab === 'json'} onClick={() => setActiveTab('json')}>JSON</TabButton>
      </div>

      {/* Basic Tab */}
      {activeTab === 'basic' && (
        <div role="tabpanel">
          <div style={fieldStyle}>
            <label htmlFor="consumer-username" style={labelStyle}>{t('gateway.consumers.form.username.label')}</label>
            <input
              id="consumer-username"
              {...form.register('username')}
              placeholder="my-consumer"
              aria-invalid={!!form.formState.errors.username}
              aria-describedby={form.formState.errors.username ? 'consumer-username-error' : undefined}
              style={inputStyle(!!form.formState.errors.username)}
            />
            {form.formState.errors.username && (
              <span id="consumer-username-error" role="alert" style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                {form.formState.errors.username.message}
              </span>
            )}
          </div>

          <div style={fieldStyle}>
            <label htmlFor="consumer-customId" style={labelStyle}>{t('gateway.consumers.form.customId.label')}</label>
            <input
              id="consumer-customId"
              {...form.register('customId')}
              placeholder="ext-id-12345"
              style={inputStyle(false)}
            />
          </div>

          <div style={fieldStyle}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)', fontSize: 'var(--font-size-sm)' }}>
              <input type="checkbox" {...form.register('enabled')} />
              {t('gateway.consumers.form.enabled.label')}
            </label>
          </div>
        </div>
      )}

      {/* JSON Tab */}
      {activeTab === 'json' && (
        <div role="tabpanel">
          <JsonEditor value={watchedValues} readOnly height={280} ariaLabel={t('gateway.shared.json.preview')} />
        </div>
      )}
    </FormDialog>
  );
}
